import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, X, GripVertical, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function CategoriesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/categories")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const persistOrder = async (list) => {
    const order = list.map((c, i) => ({ id: c.id, display_order: i + 1 }));
    await api.patch("/admin/categories/reorder", { order });
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    setItems(next);
    try { await persistOrder(next); }
    catch { toast.error("Reorder failed"); load(); }
  };

  const remove = async (c) => {
    if (!confirm(`Delete category "${c.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Category deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="categories-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs text-charcoal/60 max-w-xl">
            Categories are used across the shop, admin product form, and AI planner sourcing.
            Reorder with the arrows to change display order on the storefront.
          </p>
        </div>
        <button
          onClick={() => { setEditing(null); setShowDialog(true); }}
          className="rounded-full px-5 py-2.5 text-sm font-bold text-white shadow-lg shadow-rose-500/40 flex items-center gap-2"
          style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
          data-testid="add-category-btn"
        >
          <Plus className="w-4 h-4" strokeWidth={2.25} /> Add category
        </button>
      </div>

      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-6 text-charcoal/60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-6 text-charcoal/60">No categories yet — add one to get started.</div>
        ) : (
          <div className="divide-y divide-warm">
            {items.map((c, i) => (
              <div key={c.id} className="p-4 flex items-center gap-4" data-testid={`category-row-${i}`}>
                <GripVertical className="w-4 h-4 text-charcoal/30" />
                <div className="w-14 h-14 rounded-xl bg-linen-2 overflow-hidden flex-shrink-0">
                  {c.image_url ? (
                    <img src={c.image_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-charcoal/30 text-xs">
                      No image
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg text-charcoal">{c.name}</p>
                  <p className="text-xs text-charcoal/50 font-mono">{c.slug}</p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    className="p-2 rounded-full hover:bg-linen-2 disabled:opacity-30"
                    data-testid={`cat-up-${i}`}
                  >
                    <ArrowUp className="w-4 h-4 text-charcoal/60" />
                  </button>
                  <button
                    onClick={() => move(i, +1)}
                    disabled={i === items.length - 1}
                    className="p-2 rounded-full hover:bg-linen-2 disabled:opacity-30"
                    data-testid={`cat-down-${i}`}
                  >
                    <ArrowDown className="w-4 h-4 text-charcoal/60" />
                  </button>
                  <button
                    onClick={() => { setEditing(c); setShowDialog(true); }}
                    className="p-2 rounded-full hover:bg-linen-2"
                    data-testid={`cat-edit-${i}`}
                  >
                    <Pencil className="w-4 h-4 text-charcoal/60" strokeWidth={1.75} />
                  </button>
                  <button
                    onClick={() => remove(c)}
                    className="p-2 rounded-full hover:bg-rose-50"
                    data-testid={`cat-delete-${i}`}
                  >
                    <Trash2 className="w-4 h-4 text-rose-500" strokeWidth={1.75} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showDialog && (
        <CategoryDialog
          initial={editing}
          onClose={() => setShowDialog(false)}
          onSaved={() => { setShowDialog(false); load(); }}
        />
      )}
    </div>
  );
}

function CategoryDialog({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [slug, setSlug] = useState(initial?.slug || "");
  const [imageUrl, setImageUrl] = useState(initial?.image_url || "");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/files/upload?folder=categories`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImageUrl(res.data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        slug: slug.trim() || undefined,
        image_url: imageUrl,
        display_order: initial?.display_order ?? 0,
      };
      if (initial) await api.patch(`/admin/categories/${initial.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success(initial ? "Category updated" : "Category added");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/50 backdrop-blur-sm"
      onClick={onClose} data-testid="category-dialog">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-3xl max-w-lg w-full shadow-2xl">
        <header className="p-6 border-b border-warm flex items-center justify-between">
          <p className="font-display text-2xl text-charcoal">
            {initial ? "Edit category" : "Add category"}
          </p>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-linen-2">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-6 space-y-4">
          <Field label="Name *">
            <input
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400"
              placeholder="e.g. Chocolates"
              data-testid="cat-name"
            />
          </Field>
          <Field label={initial ? "Slug (locked)" : "Slug (auto if blank)"}>
            <input
              disabled={!!initial}
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
              className="w-full rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400 disabled:bg-linen-2 disabled:text-charcoal/50 font-mono text-sm"
              placeholder="e.g. chocolates"
              data-testid="cat-slug"
            />
          </Field>
          <Field label="Image (optional)">
            <div className="flex items-center gap-3">
              <input
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="Paste URL or click Upload →"
                className="flex-1 rounded-xl bg-white border border-warm px-4 py-3 focus:outline-none focus:border-rose-400 text-sm"
                data-testid="cat-image-url"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="rounded-full px-4 py-3 text-xs font-bold bg-charcoal text-white shadow-md disabled:opacity-50 whitespace-nowrap"
                data-testid="cat-image-upload"
              >
                {uploading ? "Uploading…" : "Upload"}
              </button>
              <input ref={fileRef} type="file" hidden accept="image/*" onChange={upload} />
              {imageUrl && (
                <img src={imageUrl} alt="" className="w-11 h-11 rounded-lg object-cover border border-warm" />
              )}
            </div>
          </Field>
        </div>
        <footer className="p-6 border-t border-warm flex justify-end gap-3">
          <button type="button" onClick={onClose} className="pill-btn pill-btn-secondary text-sm" data-testid="cat-cancel">
            Cancel
          </button>
          <button type="submit" disabled={saving}
            className="rounded-full px-6 py-3 text-sm font-bold text-white shadow-lg shadow-rose-500/40 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #E11D48 0%, #A855F7 100%)' }}
            data-testid="cat-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </footer>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-[0.2em] font-semibold text-charcoal/60 mb-2 block">
      {label}
    </label>
    {children}
  </div>
);
